import { useState, useEffect, useCallback, useMemo } from "react";

import api from "../../lib/api";

import { Button } from "../../components/ui/Button";

import {

  ModalidadConfigTable,

  type ModalidadConfigRow,

} from "../../components/ModalidadConfigTable";



interface ModalidadRow {

  id: number;

  nombre: string;

  tipo: string;

  precio_base: number;

  precio_personalizado: number | null;

  precio_venta: number | null;

  precio_minimo: number;

  activo: boolean;

}



function rowsToEditable(rows: ModalidadRow[]): ModalidadConfigRow[] {

  return rows.map((r) => ({

    id: r.id,

    nombre: r.nombre,

    tipo: r.tipo,

    precio_minimo: r.precio_minimo,

    activo: r.activo,

    precioVentaInput: r.precio_venta != null ? String(r.precio_venta) : "",

  }));

}



function rowsEqual(a: ModalidadConfigRow[], b: ModalidadConfigRow[]): boolean {

  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {

    const rowA = a[i];

    const rowB = b[i];

    if (rowA.id !== rowB.id) return false;

    if (rowA.activo !== rowB.activo) return false;

    if (rowA.precioVentaInput.trim() !== rowB.precioVentaInput.trim()) return false;

  }

  return true;

}



export function MisModalidadesPage() {

  const [rows, setRows] = useState<ModalidadConfigRow[]>([]);

  const [savedRows, setSavedRows] = useState<ModalidadConfigRow[]>([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [success, setSuccess] = useState<string | null>(null);



  const fetchModalidades = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const res = await api.get<ModalidadRow[]>("/cliente/modalidades");

      const data = Array.isArray(res.data) ? res.data : [];

      const editable = rowsToEditable(data);

      setRows(editable);

      setSavedRows(editable);

    } catch {

      setError("No se pudieron cargar las modalidades.");

      setRows([]);

      setSavedRows([]);

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    void fetchModalidades();

  }, [fetchModalidades]);



  const hasChanges = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows]);



  const handleChange = (id: number, field: "activo" | "precioVenta", value: boolean | string) => {

    setSuccess(null);

    setRows((prev) =>

      prev.map((r) => {

        if (r.id !== id) return r;

        if (field === "activo") return { ...r, activo: value as boolean };

        return { ...r, precioVentaInput: value as string };

      })

    );

  };



  const handleSave = async () => {

    setError(null);

    setSuccess(null);



    for (const row of rows) {

      const trimmed = row.precioVentaInput.trim();

      if (!trimmed) continue;

      const n = parseFloat(trimmed);

      if (Number.isNaN(n) || n < row.precio_minimo) {

        setError(

          `El precio de "${row.nombre}" no puede ser inferior a ${row.precio_minimo.toFixed(2)} €`

        );

        return;

      }

    }



    setSaving(true);

    try {

      const payload = rows.map((row) => {

        const trimmed = row.precioVentaInput.trim();

        return {

          modalidad_id: row.id,

          activo: row.activo,

          precio_venta: trimmed ? parseFloat(trimmed) : null,

        };

      });

      await api.put("/cliente/modalidades", payload);

      const refreshed = rowsToEditable(

        rows.map((row) => ({

          id: row.id,

          nombre: row.nombre,

          tipo: row.tipo,

          precio_base: row.precio_minimo,

          precio_personalizado: null,

          precio_venta: row.precioVentaInput.trim() ? parseFloat(row.precioVentaInput.trim()) : null,

          precio_minimo: row.precio_minimo,

          activo: row.activo,

        }))

      );

      setRows(refreshed);

      setSavedRows(refreshed);

      setSuccess("Cambios guardados correctamente.");

    } catch (err: unknown) {

      setError(

        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??

          "No se pudieron guardar los cambios."

      );

    } finally {

      setSaving(false);

    }

  };



  return (

    <div className="space-y-6">

      <div className="flex flex-wrap items-start justify-between gap-4">

        <div className="space-y-2">

          <h1 className="text-2xl font-semibold text-[#2C2C2C]">Mis modalidades</h1>

          <p className="max-w-2xl text-sm text-gray-600">

            Activa las modalidades que quieres ofrecer y establece tu precio de venta. El precio

            mínimo es el precio base acordado con Circuit Cover.

          </p>

        </div>

        <Button loading={saving} disabled={!hasChanges || loading || rows.length === 0} onClick={handleSave}>

          Guardar cambios

        </Button>

      </div>



      {error && <p className="text-sm text-red-600">{error}</p>}

      {success && <p className="text-sm text-green-700">{success}</p>}



      {loading ? (

        <div className="py-12 text-center text-gray-500">Cargando…</div>

      ) : rows.length === 0 ? (

        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">

          No tienes modalidades configuradas. Contacta con el administrador para activar modalidades

          en tu cuenta.

        </div>

      ) : (

        <ModalidadConfigTable modalidades={rows} onChange={handleChange} checkLabel="Activo" />

      )}

    </div>

  );

}

